/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import { getBrowserDetails } from '@oat-sa-private/ui-core';
import { preventedShortcuts } from './shortcuts.js';

const platforms = Object.freeze({
    all: 'all',
    win: 'win',
    mac: 'mac'
});

const browsers = Object.freeze({
    all: 'all',
    chrome: 'chrome',
    firefox: 'firefox',
    safari: 'safari'
});

function getCurrentPlatform(osName) {
    const normalizedOsName = osName?.toLowerCase();
    if (normalizedOsName?.includes('win')) {
        return platforms.win;
    }
    if (normalizedOsName?.includes('mac')) {
        return platforms.mac;
    }
    return platforms.all;
}

function getCurrentBrowser(browserName) {
    const normalizedBrowserName = browserName?.toLowerCase();
    if (normalizedBrowserName?.includes('firefox')) {
        return browsers.firefox;
    }
    if (normalizedBrowserName?.includes('chrome')) {
        return browsers.chrome;
    }
    if (normalizedBrowserName?.includes('safari')) {
        return browsers.safari;
    }
    return browsers.all;
}

function matchesCurrentPlatform(itemPlatform, currentPlatform) {
    return currentPlatform === platforms.all || typeof itemPlatform === 'undefined' || itemPlatform === currentPlatform;
}

function matchesCurrentBrowser(itemBrowser, currentBrowser) {
    return (
        currentBrowser === browsers.all ||
        typeof itemBrowser === 'undefined' ||
        itemBrowser === browsers.all ||
        itemBrowser === currentBrowser
    );
}

function matchesWhitelistedBrowser(whitelistBrowser, currentBrowser) {
    return (
        currentBrowser === browsers.all ||
        typeof whitelistBrowser === 'undefined' ||
        whitelistBrowser === browsers.all ||
        whitelistBrowser === currentBrowser
    );
}

/**
 * Get the list of unique shortcuts applicable to the current platform and browser
 * @param {object[]} whitelist the array of whitelisted shortcuts
 * @returns {object[]} shortcuts
 */
export function getPreventedPlatformShortcuts(whitelist = []) {
    const { os, browser } = getBrowserDetails(window.navigator.userAgent);
    const currentPlatform = getCurrentPlatform(os?.name);
    const currentBrowser = getCurrentBrowser(browser?.name);

    const isInWhitelist = shortcutItem =>
        whitelist.some(
            whitelistItem =>
                whitelistItem.shortcut === shortcutItem.shortcut &&
                matchesCurrentPlatform(whitelistItem.platform, currentPlatform) &&
                matchesWhitelistedBrowser(whitelistItem.browser, currentBrowser)
        );

    const shortcuts = preventedShortcuts.filter(item => {
        if (isInWhitelist(item)) {
            return false;
        }
        return matchesCurrentPlatform(item.platform, currentPlatform) && matchesCurrentBrowser(item.browser, currentBrowser);
    });

    // Build a collection of `shortcut` objects with distinct shortcut values
    return [...new Map(shortcuts.map(item => [item['shortcut'], item])).values()];
}
